import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import React from "react";

import { useLocalStorage } from "../../hooks";

import { XMarkIcon } from "@heroicons/react/20/solid";
import { useDispatch } from "react-redux";
import marker_img from "../../assets/location.svg";
import { TwButton, TwInputGroup, TwLocationInput } from "../../components/tailwind";
import { useToast } from "../../provider/ToastProvider";
import { setActionLoading, setGlobalLoading } from "../../redux/reducers/loadingReducer";
import axiosInstance from "../../utils/axiosInstance";
import { getUrlForUsers } from "../../utils/confighelpers";
import { useTranslation } from "react-i18next";


export default function AddEditLocationModal({
  open,
  handleClose,
  handleUpdate,
  isEdit,
  selectedLocationData,
}) {
  const { t, i18n } = useTranslation();
  const { addErrorToast } = useToast();
  const dispatch = useDispatch();
  const [userToken] = useLocalStorage("userToken");
  const [locationName, setLocationName] = React.useState("");
  const [postalAddress, setPostalAddress] = React.useState({});
  const [formErrors, setFormErrors] = React.useState({});

  // const postAddress = async (placeDetails, placeGeocode, coordinates) => {
  const postAddress = async () => {
    const errors = {};
    if (!locationName) {
      errors.locationName = t("errors.required");
    }
    if (!Object.keys(selectedLocationData).length && !Object.keys(postalAddress).length) {
      errors.postalAddress = t("errors.required");
    }
    setFormErrors({ ...errors });
    if (Object.keys(errors).length > 0) {
      addErrorToast(t("errors.validationGeneralError"), { hideAfter: 3 });
      return;
    }
    let body = {}
    if (isEdit === true) {
      body = {
        ...selectedLocationData,
        locationId: selectedLocationData._id
      }
    }

    if (Object.keys(postalAddress).length) {
      let placeDetails = postalAddress.placeDetails;
      let placeGeocode = postalAddress.placeGeocode;
      let coordinates = postalAddress.coordinates;
      body = {
        ...body,
        addressType: "current",
        address: placeDetails.description,
        houseNumber: placeDetails.structured_formatting.main_text,
        city: placeGeocode.address_components[
          placeGeocode.address_components.length - 3
        ].long_name,
        zip: placeGeocode.address_components[
          placeGeocode.address_components.length - 1
        ].types.find((type) => type.startsWith("postal"))
          ? placeGeocode.address_components[
            placeGeocode.address_components.length - 1
          ].long_name
          : "",
        addressName: locationName,
        location: {
          type: "Point",
          coordinates,
        },
      };
    }

    const config = {
      method: isEdit === true ? 'put' : "post",
      url: isEdit === true ? `${getUrlForUsers()}updateLocation` : `${getUrlForUsers()}addLocation`,
      data: body,
    };

    dispatch(setGlobalLoading(true));
    dispatch(setActionLoading(true));
    const addLocationRequest = await axiosInstance(config);
    dispatch(setGlobalLoading(false));
    dispatch(setActionLoading(false));
    if (addLocationRequest.data.statusCode === 200) {
      handleClose();
      setLocationName("");
      setPostalAddress({});
      handleUpdate();
    } else if (addLocationRequest.data.statusCode !== 200) {
      if (addLocationRequest.data.statusCode === 500) {
        addErrorToast(addLocationRequest.data.message);
        return;
      }
      addErrorToast(t("errors.errorAddingLocation"));
      return;
    }

  };

  React.useEffect(() => {
    if (isEdit && isEdit === true) {
      setPostalAddress({
        placeDetails: {
          description: selectedLocationData.address,
          structured_formatting: { main_text: selectedLocationData.houseNumber },
        },
        placeGeocode: {
          address_components: [
            {
              long_name: selectedLocationData.address,
              short_name: selectedLocationData.address,
              types: ["locality", "political"],
            },
            {
              long_name: selectedLocationData.address,
              short_name: selectedLocationData.address,
              types: ["locality", "political"],
            },
            {
              long_name: selectedLocationData.address,
              short_name: selectedLocationData.address,
              types: ["locality", "political"],
            },
            {
              ...(selectedLocationData?.zip && selectedLocationData.zip !== "") ? {
                long_name: selectedLocationData.zip,
                short_name: selectedLocationData.zip,
                types: ["postal_code"],
              } : {
                long_name: selectedLocationData.address,
                short_name: selectedLocationData.address,
                types: ["locality", "political"],
              }
            },
          ]
        },
        coordinates: selectedLocationData.location.coordinates
      });
      setLocationName(selectedLocationData.addressName)

    } else {
      setPostalAddress({})
      setLocationName("")
    }
  }, [isEdit, selectedLocationData]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      className="relative z-50"
    >
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-md sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
          >
            <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
              <button
                type="button"
                onClick={handleClose}
                className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                <span className="sr-only">{t("inputFieldsTitle.close")}</span>
                <XMarkIcon aria-hidden="true" className="size-6" />
              </button>
            </div>
            <div className="flex flex-col gap-4">
              <DialogTitle as="h3" className="text-base font-semibold text-gray-900">
                {isEdit ? t("inputFieldsTitle.updateLocation") : t("inputFieldsTitle.addLocation")}
              </DialogTitle>

              <TwLocationInput
                label={t("inputFieldsTitle.businessAddress")}
                name={"postalAddress"}
                leftIcon={(<img className={""} src={marker_img} alt={""} />)}
                defaultLocationSelect={postalAddress?.placeDetails}
                onLocationSelect={(data, postAdd) => {

                  // setPostalAddress({ placeDetails: e, placeGeocode: placeDetails, coordinates: coordinates });
                  setPostalAddress(postAdd);

                  // setAddress(data);
                }}
                errors={formErrors}
              />
              <TwInputGroup
                label={t("inputFieldsTitle.locationName")}
                name={"locationName"}
                // placeholder={t("inputFieldsTitle.locationName")}
                value={locationName}
                onChange={(e) => {
                  setLocationName(e.target.value);
                }}
                errors={formErrors}
              />
            </div>
            <div className="mt-5 sm:mt-4 flex flex-col gap-3 sm:flex-row-reverse ">
              <TwButton
                variant={"secondary"}
                className="w-full sm:w-auto"
                // disabled={loading}
                onClick={() => {
                  postAddress();
                }}
              >
                {isEdit ? t("inputFieldsTitle.updateLocation") : t("inputFieldsTitle.addLocation")}
              </TwButton>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
